import React, { useState } from 'react'
import MarkdownRenderer from '@/components/common/MarkdownRenderer'
import { ChevronDown, ChevronUp } from 'lucide-react'
import defaultAiAvatar from '@/assets/images/chat/avatar_ai.png'
import Icon from '@/components/Icon/Icon'

export interface ChatMessageProps {
  content: string
  isUser: boolean
  timestamp: Date
  avatar?: string
  isLoading?: boolean
}

/**
 * 聊天消息组件 - 2025年现代ChatGPT风格
 * AI回答在左边，用户回答在右边，用户消息带浅色气泡
 */
const ChatMessage: React.FC<ChatMessageProps> = ({
  content,
  isUser,
  avatar,
  isLoading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [showCopySuccess, setShowCopySuccess] = useState(false)
  const contentRef = React.useRef<HTMLDivElement>(null)

  // 检查内容是否超出高度限制
  React.useEffect(() => {
    if (contentRef.current) {
      const element = contentRef.current
      const maxHeight = 200 // 最大高度限制
      const isOverflow = element.scrollHeight > maxHeight
      setIsOverflowing(isOverflow)

      // 如果内容不超出，确保展开状态为 false
      if (!isOverflow) {
        setIsExpanded(false)
      }
    }
  }, [content])

  // 复制到剪贴板函数
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setShowCopySuccess(true)
      setTimeout(() => setShowCopySuccess(false), 2000)
    } catch (err) {
      console.error('复制失败:', err)
    }
  }

  if (isUser) {
    // 用户消息 - 右对齐，带浅色气泡
    return (
      <div className="group relative">
        <div className="flex justify-end gap-4 px-6 py-4">
          {/* 消息内容区域 */}
          <div className="flex-1 min-w-0 space-y-2 max-w-2xl">
            <div className="prose prose-sm max-w-none text-right">
              <div className="inline-block text-left">
                <div className="relative">
                  <div
                    ref={contentRef}
                    className={`leading-6 whitespace-pre-wrap font-normal text-gray-900 bg-purple-50 border border-purple-200 rounded-2xl px-4 py-3 transition-all duration-200 ${
                      isExpanded ? '' : 'max-h-[200px] overflow-hidden'
                    }`}
                  >
                    {content}
                  </div>

                  {/* 渐变遮罩 - 只在收起状态且内容溢出时显示 */}
                  {!isExpanded && isOverflowing && (
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-purple-50 via-purple-50/90 via-purple-50/50 to-transparent pointer-events-none rounded-b-2xl"></div>
                  )}
                </div>

                {/* 显示完整信息按钮 */}
                {isOverflowing && (
                  <div className="mt-3 text-center relative z-10">
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 transition-colors bg-white px-3 py-1 rounded-full shadow-sm border border-purple-200 hover:border-purple-300"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp size={12} />
                          收起
                        </>
                      ) : (
                        <>
                          <ChevronDown size={12} />
                          显示完整信息
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* 复制按钮 */}
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors bg-white px-2 py-1 rounded-full shadow-sm border border-gray-200 hover:border-gray-300"
                    title="复制消息"
                  >
                    <Icon name="copy" size={20} />
                    {showCopySuccess ? '已复制' : '复制'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 头像区域 */}
          <div className="flex-shrink-0">
            <div className="w-[35px] h-[35px] rounded-xl flex items-center justify-center text-xs font-semibold bg-gradient-to-br from-purple-500 via-purple-600 to-blue-600 text-white shadow-md relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
              <span className="relative z-10">你</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // AI消息 - 左对齐，消息内容带白色背景气泡
  return (
    <div className="group relative">
      <div className="flex gap-4 px-6 py-4">
        {/* 头像区域 */}
        <div className="flex-shrink-0">
          <img
            src={avatar || defaultAiAvatar}
            alt="AI"
            className="w-[35px] h-[35px] rounded-lg object-cover"
            onError={(e) => {
              // 如果图片加载失败，使用默认头像
              e.currentTarget.src = defaultAiAvatar
            }}
          />
        </div>

        {/* 消息内容区域 - 白色气泡背景 */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
            {isLoading ? (
              <div className="flex items-center gap-3 py-1">
                <div className="flex space-x-1">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: '0.1s' }}
                  ></div>
                  <div
                    className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: '0.2s' }}
                  ></div>
                </div>
                <span className="text-sm text-gray-500 font-normal">
                  正在思考...
                </span>
              </div>
            ) : (
              <MarkdownRenderer
                content={content}
                className="text-gray-900 leading-7 font-normal"
              />
            )}
          </div>

          {/* 复制按钮 */}
          {!isLoading && (
            <div className="flex justify-start">
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors bg-white px-2 py-1 rounded-full shadow-sm border border-gray-200 hover:border-gray-300"
                title="复制消息"
              >
                <Icon name="copy" size={20} />
                {showCopySuccess ? '已复制' : '复制'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChatMessage
